import omit from 'lodash-es/omit';
import { useCallback } from 'react';
import { v4 as uuid } from 'uuid';

import { toast } from '@/common/components/shared/toaster/toast';
import {
  useCreateEmployeeVisaMutation,
  useDeleteEmployeeVisaMutation,
  useUpdateEmployeeVisaMutation,
} from '@/common/graphql';
import type { EmployeeVisaType } from '@/common/types';
import { ApiUtils, StringUtils } from '@/common/utils';

interface VisaPersistence {
  employeeId: string;
  visas: EmployeeVisaType[];
  onVisasChange: (data: EmployeeVisaType[]) => void;
  // this triggers Mutation to be triggered, otherwise we let user
  // handle manually the data that was modified (e.g. to be saved later)
  autoPersistInDatabase?: boolean;
}

export const useVisaPersistence = ({
  employeeId,
  visas = [],
  onVisasChange,
  autoPersistInDatabase = true,
}: VisaPersistence) => {
  const [createRecord] = useCreateEmployeeVisaMutation();
  const [updateRecord] = useUpdateEmployeeVisaMutation();
  const [deleteRecord] = useDeleteEmployeeVisaMutation();

  const onVisaCreate = useCallback(
    async ({ ...formData }: EmployeeVisaType) => {
      if (autoPersistInDatabase) {
        await createRecord({
          variables: {
            input: {
              employeeId,
              ...omit(formData, 'id'),
              visaIssuedDate: StringUtils.emptyToNull(formData.visaIssuedDate),
              visaExpiryDate: StringUtils.emptyToNull(formData.visaExpiryDate),
              workPermitIssuedDate: StringUtils.emptyToNull(formData.workPermitIssuedDate),
              workPermitExpiryDate: StringUtils.emptyToNull(formData.workPermitExpiryDate),
            },
          },
          onCompleted: data => {
            toast.success();
            onVisasChange([...visas, { ...formData, id: data.createEmployeeVisa.id }]);
          },
          onError: error => ApiUtils.handleApiError(error),
        });
      } else {
        onVisasChange([...visas, { ...formData, id: uuid() }]);
      }
    },
    [autoPersistInDatabase, visas, createRecord, employeeId, onVisasChange],
  );

  const onVisaUpdate = useCallback(
    async (formData: EmployeeVisaType) => {
      if (autoPersistInDatabase) {
        await updateRecord({
          variables: {
            input: {
              ...formData,
              visaIssuedDate: StringUtils.emptyToNull(formData.visaIssuedDate),
              visaExpiryDate: StringUtils.emptyToNull(formData.visaExpiryDate),
              workPermitIssuedDate: StringUtils.emptyToNull(formData.workPermitIssuedDate),
              workPermitExpiryDate: StringUtils.emptyToNull(formData.workPermitExpiryDate),
            },
          },
          onCompleted: () => {
            toast.success();
            onVisasChange([...visas.map(item => (item.id === formData.id ? { ...item, ...formData } : item))]);
          },
          onError: error => ApiUtils.handleApiError(error),
        });
      } else {
        onVisasChange([...visas.map(item => (item.id === formData.id ? { ...item, ...formData } : item))]);
      }
    },
    [autoPersistInDatabase, visas, onVisasChange, updateRecord],
  );

  const onVisaRemove = useCallback(
    async (id: string) => {
      if (autoPersistInDatabase) {
        await deleteRecord({
          variables: { id: id },
          onCompleted: () => {
            toast.success();
            onVisasChange(visas.filter(item => item.id !== id));
          },
          onError: error => ApiUtils.handleApiError(error),
        });
      } else {
        onVisasChange(visas.filter(item => item.id !== id));
      }
    },
    [autoPersistInDatabase, visas, deleteRecord, onVisasChange],
  );

  return {
    onVisaCreate,
    onVisaUpdate,
    onVisaRemove,
  };
};
